/**
 * 店舗関連のAPIエンドポイント
 *
 * Note:
 * - getStore, updateStore (自店舗): 通常のユーザー権限で使用可能
 * - その他のメソッド: システム管理者権限が必要
 */

import apiClient from '../client';
import type { Store, StoreCodeResponse, StorePaginatedResponse } from '../../../types';

export interface StoreListParams {
  storeId?: number;
  skip?: number;
  limit?: number;
  search?: string;
  contractType?: number;
  status?: boolean;
  region?: string;
  sort?: 'name' | 'contract_start_date' | 'monthly_fee';
  order?: 'asc' | 'desc';
  includeContract?: boolean;
}

export const storeApi = {
  /**
   * 店舗一覧取得（ページネーション付き）
   */
  getStores: async (params?: StoreListParams): Promise<StorePaginatedResponse> => {
    // クエリパラメータを構築
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.storeId !== undefined) queryParams.append('store_id', params.storeId.toString());
      if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
      if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.contractType !== undefined)
        queryParams.append('contract_type', params.contractType.toString());
      if (params.status !== undefined) queryParams.append('status', params.status.toString());
      if (params.region) queryParams.append('region', params.region);
      if (params.sort) queryParams.append('sort', params.sort);
      if (params.order) queryParams.append('order', params.order);
      if (params.includeContract !== undefined)
        queryParams.append('include_contract', params.includeContract.toString());
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/stores?${queryString}` : '/stores';

    return apiClient.adminRequest<StorePaginatedResponse>(url, { method: 'GET' });
  },

  /**
   * 店舗詳細取得
   * 通常のユーザー（店舗管理者）も自店舗の情報を取得可能
   */
  getStore: async (storeId: number): Promise<Store> => {
    return apiClient.get<Store>(`/stores/${storeId}`);
  },

  /**
   * 店舗コード生成
   *
   * 新規店舗用の一意なコードを生成します。
   * 形式: ST + 5桁の連番（例: ST00001）
   * 有効期限: 生成から30分
   */
  generateCode: async (): Promise<StoreCodeResponse> => {
    return apiClient.adminRequest<StoreCodeResponse>('/stores/generate-code', { method: 'GET' });
  },

  /**
   * 店舗情報更新
   * 通常のユーザー（店舗管理者）も自店舗の情報を更新可能
   */
  updateStore: async (storeId: number, data: Partial<Store>): Promise<Store> => {
    return apiClient.put<Store>(`/stores/${storeId}`, data);
  },

  /**
   * 店舗作成
   */
  createStore: async (data: Omit<Store, 'id' | 'createdAt' | 'updatedAt'>): Promise<Store> => {
    return apiClient.adminRequest<Store>('/stores', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  /**
   * 全店舗の地域（都道府県）リストを取得
   *
   * @param activeOnly アクティブな店舗のみから取得するか（デフォルト: true）
   * @returns 重複を除いた都道府県のリスト（ソート済み）
   */
  getRegions: async (activeOnly: boolean = true): Promise<string[]> => {
    const queryParams = new URLSearchParams();
    queryParams.append('active_only', activeOnly.toString());
    return apiClient.adminRequest<string[]>(`/stores/regions?${queryParams.toString()}`, {
      method: 'GET'
    });
  }
};
